/**
 * Client-safe approval card fetch + shared TanStack query key.
 * Used by approval stack, ownership glance, and sidebar badge (Run 12).
 */

import type { CrewApprovalCard } from '@/lib/crew/autonomy/types'

export const CREW_APPROVAL_CARDS_QUERY_KEY = ['crew', 'approval-cards'] as const

/** One staleTime for every observer on this key (stack, ownership glance, sidebar). */
export const CREW_APPROVAL_CARDS_STALE_MS = 15_000

/** Fired when a chat plan becomes awaiting so Approvals refetch without a reload. */
export const CREW_APPROVALS_REFRESH_EVENT = 'crew-approvals-refresh' as const

export function notifyCrewApprovalsRefresh() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(CREW_APPROVALS_REFRESH_EVENT))
}

/** Detect awaiting show_plan payloads from stream annotations or server events. */
export function isAwaitingShowPlanPayload(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  const root = value as Record<string, unknown>
  const plan =
    root.show_plan && typeof root.show_plan === 'object'
      ? (root.show_plan as Record<string, unknown>)
      : root
  const status = typeof plan.status === 'string' ? plan.status : ''
  const content = typeof plan.content === 'string' ? plan.content : ''
  return (
    status === 'awaiting_approval' ||
    content.includes('_Waiting for Approve before mutate tools run._')
  )
}

export type CrewApprovalCardGroup = {
  agentId: string
  agentName: string
  cards: CrewApprovalCard[]
}

export async function fetchCrewApprovalCards(input: {
  spaceId: string
  workspaceId: string
}): Promise<{ pendingCount: number; groups: CrewApprovalCardGroup[] }> {
  const qs = new URLSearchParams({
    spaceId: input.spaceId,
    workspaceId: input.workspaceId
  })
  const res = await fetch(`/api/crew/approval-cards?${qs.toString()}`)
  if (!res.ok) {
    throw new Error(`Could not load approval cards (${res.status})`)
  }
  const data = (await res.json()) as {
    pendingCount?: number
    groups?: CrewApprovalCardGroup[]
  }
  return {
    pendingCount: data.pendingCount ?? 0,
    groups: data.groups ?? []
  }
}

/**
 * Non–React Query helper only. TanStack observers must use `fetchCrewApprovalCards`
 * as `queryFn` (full `{ pendingCount, groups }` cache) and `select` for counts.
 */
export async function fetchCrewApprovalPendingCount(input: {
  spaceId: string
  workspaceId: string
}): Promise<number> {
  const data = await fetchCrewApprovalCards(input)
  return data.pendingCount
}
